import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, TextInput } from '../components/ui.tsx';
import { Turnstile, useAppConfig } from '../components/turnstile.tsx';
import { oauthErrorMessage, SocialSignInButtons } from '../components/social-buttons.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import AuthShell from '../components/auth-shell.tsx';
import { browserTimezone } from '../forms/timezones.ts';
import { activeLanguage } from '../i18n/index.ts';
import { getDateLocale } from '../i18n/locale.ts';
import ServiceModeChoice from '../components/service-mode-choice.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { PLANS, type ServiceMode } from '@shared/constants/index.ts';
import { isAcceptableSignupPhone } from '@shared/signup-phone.ts';
import { readAttributionCookie } from '@shared/attribution-cookie.ts';
import type { HeardAboutChoice } from '@shared/heard-about.ts';
import HeardAboutField from '../components/heard-about-field.tsx';

export default function RegisterPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('auth.register.documentTitle'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSession } = useSession();

  // A social signup that could not be completed comes back here now rather than
  // to /login, so this page has to be able to say what happened.
  const socialError = oauthErrorMessage(searchParams.get('oauth_error'), searchParams.get('oauth_provider'));

  const config = useAppConfig();
  const siteKey = config.data?.turnstileSiteKey ?? null;
  const trialDays = config.data?.trialDays ?? 14;
  // The cheapest tier, from the deployment's own catalog (with the shipped
  // catalog standing in until /config answers): "plans start at" is a Starter
  // fact, and the trial itself is a trial of everything.
  const pricing = config.data?.plan ?? { currencyCode: DEFAULT_CURRENCY_CODE, plans: PLANS };
  const startingPrice = formatCents(pricing.plans.starter.basePriceCents, pricing.currencyCode, getDateLocale());
  // On a demo deployment the "14-day free trial" promise is simply false, so
  // the subtitle says what the account actually is instead. The banner above
  // the form (AuthShell) carries the warning itself.
  const demoMode = config.data?.demoMode ?? false;
  const demoExpirationDays = config.data?.demoExpirationDays ?? 0;

  const [form, setForm] = useState({ companyName: '', fullName: '', email: '', phone: '', password: '' });
  // Nothing preselected: the account's starter library and what its first
  // quote assumes both hang on this, and a pre-ticked option is an answer
  // nobody gave.
  const [serviceMode, setServiceMode] = useState<ServiceMode | null>(null);
  const [heardAbout, setHeardAbout] = useState<HeardAboutChoice | ''>('');
  const [heardAboutDetail, setHeardAboutDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);

  function set(key: keyof typeof form) {
    return (event: { target: { value: string } }) => setForm((current) => ({ ...current, [key]: event.target.value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    // Asked here rather than left to the server: the field is optional in the
    // API — an older client or a scripted signup must still be able to create
    // an account — so this form is the only thing that can insist.
    if (!serviceMode) {
      setError(null);
      setFieldErrors({ serviceMode: t('auth.register.serviceModeRequired') });
      return;
    }

    // Optional, so only a number that was actually typed is checked. The API
    // applies the same rule; checking here saves the round trip.
    if (form.phone.trim() && !isAcceptableSignupPhone(form.phone)) {
      setError(null);
      setFieldErrors({ phone: t('auth.register.phoneInvalid') });
      return;
    }

    setBusy(true);
    setError(null);
    setFieldErrors({});

    try {
      const timezone = browserTimezone();
      const attribution = readAttributionCookie(document.cookie);
      const { phone, ...account } = form;
      const session = await authApi.register({
        ...account,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        // Better than the server's fallback zone for everyone not in it, and
        // changeable afterwards in Settings.
        ...(timezone ? { timezone } : {}),
        // The account's starter rooms, tasks, payment methods and quote terms
        // are seeded as database rows, so they can only be written in the
        // language this form is being read in if that language travels with
        // the signup.
        language: activeLanguage(),
        // Which starter library the account is created with, and what its
        // first quote assumes.
        serviceMode,
        ...(turnstileToken ? { turnstileToken } : {}),
        // The cookie is the first visit. Sending it here is what the account
        // row is written from; the server also reads the cookie if this is
        // missing.
        ...(attribution ? { attribution } : {}),
        ...(heardAbout ? { heardAbout } : {}),
        ...(heardAbout === 'other' && heardAboutDetail.trim() ? { heardAboutDetail: heardAboutDetail.trim() } : {}),
      });
      setSession(session);
      // A brand-new account has no factor, so where the deployment mandates
      // one this is the first moment the mandate applies. Same landing as a
      // sign-in that owes enrollment: the section draws its own notice from
      // the server's state.
      navigate(session.mfaSetupRequired ? '/settings/security' : '/', {
        replace: true,
      });
    } catch (caught) {
      // A Turnstile token is single-use, so a failed attempt needs a fresh one.
      setTurnstileReset((value) => value + 1);
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFieldErrors(caught.fields);
      } else {
        setError(t('auth.register.networkError'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={t('auth.register.title')}
      wide
      subtitle={
        demoMode
          ? t('auth.register.demoSubtitle', { count: demoExpirationDays })
          : t('auth.register.subtitle', { trialDays, base: startingPrice })
      }
    >
      {socialError ? (
        <div className="mb-4">
          <Alert tone="error">{socialError}</Alert>
        </div>
      ) : null}

      {/* Failures come back to /register, so canceling at the provider does not
          drop a would-be new customer onto the sign-in page instead. */}
      <SocialSignInButtons intent="sign-up" failTo="/register" />

      <form onSubmit={handleSubmit} noValidate>
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}

        <TextInput
          label={t('auth.register.companyNameLabel')}
          name="companyName"
          autoComplete="organization"
          required
          value={form.companyName}
          error={fieldErrors.companyName}
          onChange={set('companyName')}
        />
        <TextInput
          label={t('auth.register.yourNameLabel')}
          name="fullName"
          autoComplete="name"
          required
          value={form.fullName}
          error={fieldErrors.fullName}
          onChange={set('fullName')}
        />
        <TextInput
          label={t('auth.register.emailLabel')}
          type="email"
          name="email"
          autoComplete="username"
          required
          value={form.email}
          error={fieldErrors.email}
          onChange={set('email')}
        />
        <TextInput
          label={t('auth.register.phoneLabel')}
          type="tel"
          name="phone"
          autoComplete="tel"
          hint={t('auth.register.phoneHint')}
          value={form.phone}
          error={fieldErrors.phone}
          onChange={set('phone')}
        />
        <TextInput
          label={t('auth.register.passwordLabel')}
          type="password"
          name="password"
          autoComplete="new-password"
          required
          minLength={12}
          hint={t('auth.register.passwordHint')}
          value={form.password}
          error={fieldErrors.password}
          onChange={set('password')}
        />

        {/*
          The one question on this form that is not about who is signing up
          but about what the account will be: it picks the starter room and
          task library, and under "both" every new quote asks which kind of
          job it is. Changeable later in Settings, which the hint says so
          nobody stalls on it.
        */}
        <ServiceModeChoice
          name="register-service-mode"
          value={serviceMode}
          onChange={(mode) => {
            setServiceMode(mode);
            setFieldErrors((current) => {
              const next = { ...current };
              delete next.serviceMode;
              return next;
            });
          }}
          hint={t('auth.register.serviceModeHint')}
          error={fieldErrors.serviceMode}
        />

        <HeardAboutField
          choice={heardAbout}
          detail={heardAboutDetail}
          onChoice={setHeardAbout}
          onDetail={setHeardAboutDetail}
        />

        {siteKey ? <Turnstile siteKey={siteKey} onToken={setTurnstileToken} resetSignal={turnstileReset} /> : null}

        <Button type="submit" variant="primary" busy={busy} className="w-full">
          {t('auth.register.submit')}
        </Button>
      </form>

      <p className="mt-6 text-sm text-[color:var(--color-muted)]">
        {t('auth.register.alreadyHaveAccount')}{' '}
        <Link to="/login" className="text-[color:var(--color-brand)] hover:underline">
          {t('auth.register.signIn')}
        </Link>
      </p>
    </AuthShell>
  );
}
